/**
 * Verify Business Sign-Up Pending Page
 * Shown after user submits business sign-up form to prompt them to verify their email
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import fitzaaLogo from '../assets/appIcon.png';

const VerifyBusinessSignUpPendingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const email = (location.state as any)?.email || '';
  const displayName = (location.state as any)?.displayName || '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-4">
            <img src={fitzaaLogo} alt="Fitzaa" className="w-12 h-12" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Check Your Email
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            We've sent a verification link to your email address
          </p>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/50 rounded-full">
              <Mail className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            </div>
          </div>

          <div className="text-center space-y-2 mb-4">
            {email && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Verification email sent to:
              </p>
            )}
            {email && (
              <p className="text-base font-semibold text-gray-900 dark:text-white break-all">
                {email}
              </p>
            )}
          </div>

          <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
            <p className="font-medium">Next steps:</p>
            <ol className="list-decimal list-inside space-y-2 ml-2">
              <li>Check your inbox (and spam folder if needed)</li>
              <li>Click the verification link in the email</li>
              <li>You'll be asked to create a password</li>
              <li>Your account will be activated</li>
            </ol>
          </div>

          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <p className="text-xs text-yellow-800 dark:text-yellow-200">
              <strong>Note:</strong> The verification link will expire in 24 hours. If you don't receive the email, please check your spam folder or try again.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => navigate('/admin/login', { replace: true })}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Login</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyBusinessSignUpPendingPage;

